import * as Dialog from '@radix-ui/react-dialog'
import React from 'react'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import {
  Button,
  ButtonSize,
  Icon,
  WithStyleOverrides,
} from '@opallabs/lapidary'
import { ModalDialogContextProvider } from '../ModalDialogContext'
import * as styles from './styles'
import { DrawerContentProps } from './types'

export const Root = Dialog.Root
Root.displayName = 'Lapidary: Root ← Drawer'
export const Trigger = Dialog.Trigger
Trigger.displayName = 'Lapidary: Trigger ← Drawer'

const Portal: React.FC<React.ComponentProps<typeof Dialog.Portal>> = ({
  children,
  ...props
}) => {
  const theme = useTheme()

  /**
   * Dialog.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <Dialog.Portal {...props}>
      <ThemeProvider
        theme={theme}
        // Portalled components should have absolute/fixed position
        // Reset the size properties, so that the portal itself
        // does not affect the container's layout
        css={{ blockSize: 'auto', inlineSize: 'auto' }}
      >
        {children}
      </ThemeProvider>
    </Dialog.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← Drawer'

export const CLOSE_BUTTON_CLASS = 'lapidary-drawer-close'

export type HeaderProps = WithStyleOverrides &
  React.HTMLAttributes<HTMLDivElement>
export const Header: React.FC<HeaderProps> = ({ styleOverrides, ...props }) => {
  return <div {...props} css={[styles.drawerHeader, styleOverrides]} />
}
Header.displayName = 'Lapidary: Header ← Drawer'

export type BodyProps = React.HTMLAttributes<HTMLDivElement>
export const Body: React.FC<BodyProps> = ({ children, ...props }) => {
  return (
    <div {...props} css={styles.drawerBody}>
      {children}
    </div>
  )
}
Body.displayName = 'Lapidary: Body ← Drawer'

export type FooterProps = WithStyleOverrides &
  React.HTMLAttributes<HTMLDivElement>
export const Footer: React.FC<FooterProps> = ({
  children,
  styleOverrides,
  ...props
}) => {
  return (
    <div css={[styles.drawerFooter, styleOverrides]} {...props}>
      {children}
    </div>
  )
}
Footer.displayName = 'Lapidary: Footer ← Drawer'

export type TitleProps = React.ComponentPropsWithoutRef<typeof Dialog.Title>
export const Title = React.forwardRef<
  React.ElementRef<typeof Dialog.Title>,
  TitleProps
>((props, forwardedRef) => {
  return <Dialog.Title {...props} css={styles.title} ref={forwardedRef} />
})
Title.displayName = 'Lapidary: Title ← Drawer'

export type DescriptionProps = React.ComponentPropsWithoutRef<
  typeof Dialog.Description
>
export const Description = React.forwardRef<
  React.ElementRef<typeof Dialog.Title>,
  DescriptionProps
>((props, forwardedRef) => {
  return (
    <Dialog.Description
      {...props}
      css={styles.description}
      ref={forwardedRef}
    />
  )
})
Description.displayName = 'Lapidary: Description ← Drawer'

export type CloseProps = React.ComponentPropsWithoutRef<typeof Dialog.Close>
export const Close = React.forwardRef<
  React.ElementRef<typeof Dialog.Close>,
  CloseProps
>((props, forwardedRef) => (
  <Dialog.Close asChild {...props} ref={forwardedRef}>
    <Button
      ariaLabel="Close"
      className={CLOSE_BUTTON_CLASS}
      variant="ghost"
      css={styles.drawerClose}
      icon={<Icon glyph="gem-close" ariaHidden />}
      name="Close"
      size={ButtonSize.Small}
    />
  </Dialog.Close>
))
Close.displayName = 'Lapidary: Close ← Drawer'

export type ContentProps = React.ComponentProps<typeof Dialog.Content> &
  DrawerContentProps
export const Content = React.forwardRef<
  React.ElementRef<typeof Dialog.Content>,
  ContentProps
>(({ children, drawerOrigin, drawerSize, ...props }, forwardedRef) => {
  return (
    <Portal>
      <Dialog.Overlay css={styles.Overlay} />
      <ModalDialogContextProvider>
        <Dialog.Content
          {...props}
          css={styles.drawerWrapper(drawerOrigin, drawerSize)}
          data-drawer-origin={drawerOrigin}
          onInteractOutside={(e) => e.preventDefault()}
          ref={forwardedRef}
        >
          {children}
        </Dialog.Content>
      </ModalDialogContextProvider>
    </Portal>
  )
})
Content.displayName = 'Lapidary: Content ← Drawer'
